import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { Pencil } from 'lucide-react';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import {
  canActivateProduct,
  canDeactivateProduct,
} from '#/modules/crmMgmt/product/helper/productStatus';
import useProductInUseDialog from '#/modules/crmMgmt/product/hooks/useProductInUseDialog';
import useProductStatusMutation, {
  PRODUCT_ACTIVATE_CONFIRM,
  PRODUCT_DEACTIVATE_CONFIRM,
} from '#/modules/crmMgmt/product/hooks/useProductStatusMutation';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import { useCan } from '#/modules/infra/auth/useCan';

const ProductCommandBar = ({ detail, onRefetch }) => {
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const { showInUse, InUseDialogHost } = useProductInUseDialog();
  const canUpdate = useCan(PERM.PRODUCT_UPDATE);
  const canActivatePerm = useCan(PERM.PRODUCT_ACTIVATE);

  const id = detail?.id;
  const status = detail?.status;
  const updatedAt = detail?.record?.updatedAt ?? detail?.updatedAt;

  const statusMutation = useProductStatusMutation({
    id,
    getUpdatedAt: () => updatedAt,
    onInUse: showInUse,
    onAfterSuccess: async () => {
      await onRefetch?.();
    },
    onVersionConflict: () => {
      onRefetch?.();
    },
  });

  const isPending = statusMutation.isPending;
  const canActivate = canActivatePerm && canActivateProduct(status);
  const canDeactivate = canActivatePerm && canDeactivateProduct(status);

  async function handleActivate() {
    const ok = await confirm(PRODUCT_ACTIVATE_CONFIRM);
    if (!ok) {
      return;
    }
    statusMutation.mutate({ action: 'activate' });
  }

  async function handleDeactivate() {
    const ok = await confirm(PRODUCT_DEACTIVATE_CONFIRM);
    if (!ok) {
      return;
    }
    statusMutation.mutate({ action: 'deactivate' });
  }

  return (
    <>
      {ConfirmDialogHost}
      {InUseDialogHost}
      {canUpdate && id ? (
        <Button
          asChild
          variant="default"
          className={cn('document-detail-edit-action')}
        >
          <Link to={ROUTES.productEdit(id)} aria-label="編輯">
            <Pencil className="size-3.5" aria-hidden />
            編輯
          </Link>
        </Button>
      ) : null}
      {canActivate ? (
        <Button disabled={isPending} onClick={handleActivate}>
          啟用
        </Button>
      ) : null}
      {canDeactivate ? (
        <Button
          variant="destructive"
          className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
          disabled={isPending}
          onClick={handleDeactivate}
        >
          停用
        </Button>
      ) : null}
    </>
  );
};

ProductCommandBar.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    updatedAt: PropTypes.string,
    record: PropTypes.shape({
      updatedAt: PropTypes.string,
    }),
  }).isRequired,
  onRefetch: PropTypes.func,
};

export default ProductCommandBar;
